import React, { useEffect, useMemo, useState } from "react";
import type { Machine } from "../services/BackendDataService";
import {
  peekMotorMonitoringService,
  type MotorState,
  type StreamInfoCallback,
} from "../services/MotorMonitoringService";
import { useMotorStates } from "../hooks/useMotorStates";
import SubTabNav from "./common/SubTabNav";
import ServoHistoricalValues from "./ServoHistoricalValues";
import { useTheme } from "../providers/ThemeProvider";
import TreeView, { type TreeNode, type TreeTone } from "./TreeView";
import ConnectionStatus from "../dashboard/panels/ConnectionStatus";
import WaitingState from "../dashboard/panels/WaitingState";
import SeriesChart, {
  type SeriesDef,
  type AxisDef,
} from "../dashboard/panels/SeriesChart";
import { DashboardSyncProvider } from "../dashboard/DashboardSyncContext";
import { formatMetricValue } from "../dashboard/format";
import { usePanelData } from "../dashboard";
import {
  DATA_BINDING_VERSION,
  type ChannelSpec,
  type DataBinding,
  type ResolvedRange,
  type SourceSpec,
} from "../dashboard/spec";
import { cx } from "@digitalisation/enlixy-comp";

interface ServoConditionMonitoringProps {
  machineData?: Machine | null;
  /** ISO-8601 start time for historical replay; undefined = live only */
  startTime?: string;
  paused?: boolean;
  /** Called once with the stream's oldest/newest available timestamps. */
  onStreamInfo?: StreamInfoCallback;
  /**
   * Minimum ms between overview history points during replay.
   * Wider ranges pass a larger interval to keep point count manageable.
   */
  sampleIntervalMs?: number;
}

type Severity = "ok" | "warning" | "alarm";

interface AxisStatus {
  tone: TreeTone;
  label: string;
  severity: Severity;
}

/**
 * Classify a servo axis diagnostic message into a status.
 *   ""/"OK"/"Running"      → ok       (green)
 *   "Power is off"/"Idle"  → warning  (amber)
 *   anything else          → alarm    (red, e.g. "Stopped")
 */
function classifyStatus(diag?: string): AxisStatus {
  const msg = (diag ?? "").trim();
  if (!msg) return { tone: "ok", label: "", severity: "ok" };
  if (/^(ok|running|normal|ready)$/i.test(msg))
    return { tone: "ok", label: msg, severity: "ok" };
  if (/power is off|switched off|\boff\b|idle|standby|warn/i.test(msg))
    return { tone: "warning", label: msg, severity: "warning" };
  return { tone: "alarm", label: msg, severity: "alarm" };
}

const SEVERITY_RANK: Record<Severity, number> = { ok: 0, warning: 1, alarm: 2 };

/** Group id = axis id without the trailing `_MAxx` token. */
const groupIdOf = (axisId: string): string => axisId.replace(/_MA\d+$/, "");

/**
 * Friendly group label: keep only the first and last `_`-segments, dropping
 * everything in between, so
 * `P1233_F_ZBSB_LO_14A_fbInnerFramePressingDevice` → `P1233_fbInnerFramePressingDevice`
 * and `P0655_F_ZFSB_fbCigFormation` → `P0655_fbCigFormation`.
 */
const groupLabel = (groupId: string): string => {
  const parts = groupId.split("_");
  if (parts.length <= 1) return groupId;
  return `${parts[0]}_${parts[parts.length - 1]}`;
};

/** Leaf label = the axis token (e.g. "MA03"), falling back to the full id. */
const axisLabel = (axisId: string): string =>
  axisId.match(/MA\d+/)?.[0] ?? axisId;

// ─── Servo detail chart definitions (stable module-scope identities) ──────────
const TEMP_AXES: AxisDef[] = [
  { title: "°C", color: "#ef4444", opposite: false },
];
const TEMP_SERIES: SeriesDef[] = [
  {
    key: "tempMotor",
    name: "Temperature Motor",
    color: "#f97316",
    unit: "°C",
    axis: 0,
    showMarkers: true,
  },
  {
    key: "tempAmplifier",
    name: "Temperature Amplifier",
    color: "#ef4444",
    unit: "°C",
    axis: 0,
    showMarkers: true,
  },
];
const TORQUE_AXES: AxisDef[] = [
  { title: "Nm", color: "#22c55e", opposite: false },
];
const TORQUE_SERIES: SeriesDef[] = [
  {
    key: "torqueRms",
    name: "Torque RMS",
    color: "#22c55e",
    unit: "Nm",
    axis: 0,
    type: "areaspline",
    showMarkers: true,
  },
  { key: "torqueDc", name: "Torque DC", color: "#eab308", unit: "Nm", axis: 0, showMarkers: true },
];

type ServoSubTab = "overview" | "historical" | "anomaly";
const SERVO_SUBTABS: { key: ServoSubTab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "historical", label: "Historical Values" },
  { key: "anomaly", label: "Anomaly Detection" },
];

const ServoConditionMonitoring: React.FC<ServoConditionMonitoringProps> = ({
  machineData,
  startTime,
  paused,
  onStreamInfo,
  sampleIntervalMs,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;

  const [subTab, setSubTab] = useState<ServoSubTab>("overview");
  const [selectedAxisId, setSelectedAxisId] = useState<string | null>(null);

  // Same options the Motor screen passes: `onStreamInfo` lets the parent learn
  // the stream's newest timestamp so preset ranges are computed relative to the
  // data (not wall-clock), and `sampleIntervalMs` keeps point counts sane.
  const { motors, isConnected, subscribed, replaying, machineBK } =
    useMotorStates(machineData, startTime, paused, {
      onStreamInfo,
      sampleIntervalMs,
    });

  // Select an axis → tell the service to load its history (Doris + live),
  // exactly like the motor detail view.
  const selectAxis = (axisId: string | null) => {
    setSelectedAxisId(axisId);
    if (machineBK)
      peekMotorMonitoringService(machineBK)?.setSelectedMotor(axisId);
  };

  // Switching live↔range recreates the monitoring service, which loses the
  // selected axis — re-assert it so the new (Doris) service fetches history
  // for the axis that's still on screen.
  useEffect(() => {
    if (!machineBK || !selectedAxisId || !subscribed) return;
    peekMotorMonitoringService(machineBK)?.setSelectedMotor(selectedAxisId);
  }, [machineBK, selectedAxisId, subscribed, startTime]);
  const selectedAxis = selectedAxisId ? motors.get(selectedAxisId) : null;

  // In live mode, only show axes that have received data AFTER the initial
  // discovery pass. The worker resets lastUpdate to 0 at the end of discovery,
  // so only axes that subsequently receive live signals will have lastUpdate > 0.
  // In historical/range mode, show all discovered axes (Doris only returns
  // axes that have data in the requested window).
  const activeMotors = useMemo(() => {
    if (startTime) return motors; // range mode — show everything Doris found
    const filtered = new Map<string, MotorState>();
    for (const [id, motor] of motors) {
      if (motor.lastUpdate > 0) {
        filtered.set(id, motor);
      }
    }
    return filtered;
  }, [motors, startTime]);

  // Group axes by component (id without axis token) → axis leaves; collect
  // active (non-OK) messages. Group labels drop the `_F_<module>` token.
  const { treeNodes, axisCount } = useMemo(() => {
    const cmp = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true });

    const groups = new Map<string, MotorState[]>();
    for (const axis of activeMotors.values()) {
      const gid = groupIdOf(axis.id);
      const list = groups.get(gid);
      if (list) list.push(axis);
      else groups.set(gid, [axis]);
    }

    const nodes: TreeNode[] = Array.from(groups.entries())
      .sort((a, b) => cmp(a[0], b[0]))
      .map(([gid, axes]) => {
        let worst: Severity = "ok";
        const children: TreeNode[] = axes
          .slice()
          .sort((a, b) => cmp(a.id, b.id))
          .map((axis) => {
            const status = classifyStatus(axis.signals.diagMsg);
            if (SEVERITY_RANK[status.severity] > SEVERITY_RANK[worst])
              worst = status.severity;
            return {
              id: axis.id,
              label: axisLabel(axis.id),
              status: { tone: status.tone, label: status.label },
            } satisfies TreeNode;
          });

        return {
          id: gid,
          label: groupLabel(gid),
          status:
            worst === "ok"
              ? undefined
              : { tone: worst === "alarm" ? "alarm" : "warning" },
          children,
        } satisfies TreeNode;
      });

    return {
      treeNodes: nodes,
      axisCount: activeMotors.size,
    };
  }, [activeMotors]);

  // ── Not connected (live only — a date range loads from Doris regardless) ──
  if (!isConnected && !startTime) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
        <svg
          className={cx(
            "w-12 h-12",
            isDark ? "text-gray-600" : "text-gray-300",
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01M6.343 6.343a9 9 0 000 12.728"
          />
        </svg>
        <p
          className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}
        >
          NATS not connected
        </p>
      </div>
    );
  }

  // ── No machine ─────────────
  if (!machineBK) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p
          className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}
        >
          No machine selected
        </p>
      </div>
    );
  }

  const cardCls = cx(
    "rounded-xl border",
    isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
  );

  return (
    <div className="space-y-4">
      <SubTabNav
        tabs={SERVO_SUBTABS}
        activeTab={subTab}
        onTabChange={(k) => setSubTab(k as ServoSubTab)}
      />

      {subTab === "overview" && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <ConnectionStatus
              connected={axisCount > 0}
              label={
                axisCount > 0
                  ? `${axisCount} servo ${axisCount > 1 ? "axes" : "axis"} detected`
                  : subscribed
                    ? "Waiting for servo data…"
                    : "Connecting…"
              }
            />
            {replaying && (
              <span className="text-xs text-blue-500 animate-pulse">
                Replaying…
              </span>
            )}
          </div>

          {axisCount === 0 ? (
            <WaitingState
              title={
                startTime ? "Loading history…" : "Listening for servo signals…"
              }
              subject={
                startTime ? undefined : `edge-data.live.*.${machineBK}.>`
              }
              detail={
                startTime
                  ? "No servo axes have reported yet for this range."
                  : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
              {/* Tree */}
              <div className={cx(cardCls, "lg:col-span-1 p-4 flex flex-col")}>
                <h3
                  className={cx(
                    "text-sm font-bold mb-3",
                    isDark ? "text-gray-200" : "text-gray-700",
                  )}
                >
                  All axes
                </h3>
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                  <TreeView
                    nodes={treeNodes}
                    showControls
                    selectedId={selectedAxisId ?? undefined}
                    onSelect={(node) => {
                      if (!node.children || node.children.length === 0)
                        selectAxis(node.id);
                    }}
                  />
                </div>
              </div>

              {/* Servo details (right of the axis tree) */}
              <div className="lg:col-span-3">
                {selectedAxis ? (
                  <ServoDetail
                    motor={selectedAxis}
                    machineBK={machineBK ?? ""}
                    startTime={startTime}
                    isDark={isDark}
                    onClose={() => selectAxis(null)}
                  />
                ) : (
                  <div
                    className={cx(
                      cardCls,
                      "p-4 flex flex-col items-center justify-center py-14 text-center",
                      isDark ? "text-gray-500" : "text-gray-400",
                    )}
                  >
                    <span className="text-3xl mb-2">👈</span>
                    <p className="text-sm">
                      Select a servo axis to view its details
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {subTab === "historical" && (
        <ServoHistoricalValues
          machineBK={machineBK}
          startTime={startTime}
          isDark={isDark}
          treeNodes={treeNodes}
          selectedAxisId={selectedAxisId}
          onSelectAxis={selectAxis}
        />
      )}
      {subTab === "anomaly" && (
        <ServoUnderConstruction
          isDark={isDark}
          title="Anomaly Detection"
          subtitle="Automated anomaly detection is coming soon."
        />
      )}
    </div>
  );
};

// ─── Placeholder for not-yet-built sub-tabs ───────────────────────────────────
const ServoUnderConstruction: React.FC<{
  isDark: boolean;
  title: string;
  subtitle: string;
}> = ({ isDark, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center min-h-[320px] text-center">
    <svg
      className={cx(
        "w-14 h-14 mb-4",
        isDark ? "text-gray-600" : "text-gray-300",
      )}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
    <h3
      className={cx(
        "text-lg font-semibold mb-1",
        isDark ? "text-white" : "text-gray-900",
      )}
    >
      {title}
    </h3>
    <p className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}>
      {subtitle}
    </p>
  </div>
);

// ─── Servo detail (KPI chips + Temperature & Torque charts) ───────────────────

/** Build a regex that matches a NATS subject containing the motor id tokens
 *  AND ending with the given signal suffix. Motor ids use underscores
 *  (e.g. "P0655_F_ZIFO_MA12") but the NATS subject may use dots or underscores
 *  depending on the schema, so we match each id-segment separated by [._]. */
function motorSignalRegex(motorId: string, signalSuffix: string): string {
  // Build a regex from ALL motor-id tokens, not just the axis token: multiple
  // motors on the same machine share an axis number (e.g. MA01 appears under
  // P9030, P5454, P9010…), so matching on the axis alone would pull in every
  // motor's signal. Each id token is separated by "." or "_" in the subject
  // (schema A uses dots, schema B nests as `fbAxis_MA01_Device`), and tokens
  // may have extra text between them (e.g. `fbAxis_` before `MA01`), so join
  // with a permissive separator.
  const escape = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tokens = motorId.split("_").map(escape);
  // e.g. tokens [P9030, F, ZUVQ, fbSeparateBlanks, MA01] →
  //   "P9030[._].*F[._].*ZUVQ[._].*fbSeparateBlanks[._].*MA01[._].*TempMotor$"
  const body = tokens.join("[._].*");
  return `${body}[._].*${signalSuffix}$`;
}

/** Servo signal definitions for the usePanelData pipeline. */
const SERVO_SIGNAL_DEFS: {
  key: string;
  label: string;
  unit: string;
  color: string;
  suffix: string;
}[] = [
  { key: "tempMotor", label: "Temperature Motor", unit: "°C", color: "#f97316", suffix: "TempMotor" },
  { key: "tempAmplifier", label: "Temperature Amplifier", unit: "°C", color: "#ef4444", suffix: "TempAmplifier" },
  { key: "torqueRms", label: "Torque RMS", unit: "Nm", color: "#22c55e", suffix: "TorqueRmsValue" },
  { key: "torqueDc", label: "Torque DC", unit: "Nm", color: "#eab308", suffix: "TorqueDcValue" },
];

const ServoDetail: React.FC<{
  motor: MotorState;
  machineBK: string;
  startTime?: string;
  isDark: boolean;
  onClose: () => void;
}> = ({ motor, machineBK, startTime, isDark, onClose }) => {
  const s = motor.signals;

  // Extract the axis token (last segment of motor id, e.g. "MA12")
  const axisToken = useMemo(() => {
    const parts = motor.id.split("_");
    return parts[parts.length - 1];
  }, [motor.id]);

  // Build motor-scoped channels for usePanelData
  const channels = useMemo<ChannelSpec[]>(
    () =>
      SERVO_SIGNAL_DEFS.map((sig) => ({
        key: sig.key,
        label: sig.label,
        unit: sig.unit,
        color: sig.color,
        nats: { regex: motorSignalRegex(motor.id, sig.suffix) },
        doris: { sensorAliases: [sig.suffix], axisToken },
      })),
    [motor.id, axisToken],
  );

  const binding = useMemo<DataBinding>(
    () => ({
      version: DATA_BINDING_VERSION,
      machineRef: machineBK,
      source: (startTime ? { mode: "doris" } : { mode: "nats" }) as SourceSpec,
      channels,
    }),
    [machineBK, startTime, channels],
  );

  const range = useMemo<ResolvedRange>(
    () =>
      startTime
        ? { kind: "window", startMs: Date.parse(startTime), endMs: Date.now() }
        : { kind: "live" },
    [startTime],
  );

  const { series, loadDetail } = usePanelData(binding, range);

  const diagIsAlarm =
    !!s.diagMsg && !/^(ok|running|normal|ready)$/i.test(s.diagMsg.trim());

  const card = cx(
    "rounded-xl border",
    isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
  );
  const label = cx("text-xs mb-1", isDark ? "text-gray-400" : "text-gray-500");
  const value = cx(
    "text-lg font-bold",
    isDark ? "text-white" : "text-gray-900",
  );

  const chips: { label: string; value: React.ReactNode }[] = [
    {
      label: "Diagnosis Message",
      value: (
        <span
          className={
            diagIsAlarm
              ? "text-red-500"
              : isDark
                ? "text-white"
                : "text-gray-900"
          }
        >
          {s.diagMsg || "OK"}
        </span>
      ),
    },
    {
      label: "Temperature Motor",
      value: formatMetricValue(s.tempMotor, { decimals: 1, unit: "°C" }),
    },
    {
      label: "Temperature Amplifier",
      value: formatMetricValue(s.tempAmplifier, { decimals: 1, unit: "°C" }),
    },
    {
      label: "Torque RMS",
      value: formatMetricValue(s.torqueRms, { decimals: 2, unit: "Nm" }),
    },
    {
      label: "Torque DC",
      value: formatMetricValue(s.torqueDc, { decimals: 2, unit: "Nm" }),
    },
  ];

  return (
    <div className={cx(card, "p-4 sm:p-5 relative")}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close details"
        className={cx(
          "absolute top-3 right-3 p-1.5 rounded-lg transition-colors",
          isDark
            ? "text-gray-400 hover:bg-gray-700"
            : "text-gray-400 hover:bg-gray-100",
        )}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <h3
        className={cx(
          "text-base font-bold mb-3",
          isDark ? "text-gray-100" : "text-gray-800",
        )}
      >
        Servo Details
      </h3>

      {/* Identity */}
      <div
        className={cx(
          "text-sm space-y-0.5 mb-4",
          isDark ? "text-gray-300" : "text-gray-600",
        )}
      >
        <div>
          <span className="font-semibold">Motor:</span> {motor.id}
        </div>
        <div>
          <span className="font-semibold">Motor Type:</span> {s.motorTyp || "—"}
        </div>
        <div>
          <span className="font-semibold">Motor Part No.:</span>{" "}
          {s.motorPartNo || "—"}
        </div>
      </div>

      {/* KPI chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        {chips.map((c) => (
          <div key={c.label} className={cx(card, "p-3")}>
            <div className={label}>{c.label}</div>
            <div className={value}>{c.value}</div>
          </div>
        ))}
      </div>
      {motor.lastUpdate > 0 && (
        <div className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2 mb-4">
          Last update: {new Date(motor.lastUpdate).toLocaleTimeString()}
        </div>
      )}

      {/* Charts */}
      <DashboardSyncProvider>
        <div className="space-y-4">
          <div className={cx(card, "p-3")}>
            <div className="h-64">
              <SeriesChart
                title="Temperature"
                seriesDefs={TEMP_SERIES}
                yAxes={TEMP_AXES}
                data={series}
                height={250}
                canDetail={!!startTime}
                onDetail={loadDetail}
                commentType={`servo_temperature_${motor.id}`}
                machineBK={machineBK}
              />
            </div>
          </div>
          <div className={cx(card, "p-3")}>
            <div className="h-64">
              <SeriesChart
                title="Torque"
                seriesDefs={TORQUE_SERIES}
                yAxes={TORQUE_AXES}
                data={series}
                height={250}
                canDetail={!!startTime}
                onDetail={loadDetail}
                commentType={`servo_torque_${motor.id}`}
                machineBK={machineBK}
              />
            </div>
          </div>
        </div>
      </DashboardSyncProvider>
    </div>
  );
};

export default ServoConditionMonitoring;
